import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import AlpacaApi from '../../apis/alpaca_api';
import { alpacaKeys } from '../../queries/AlpacaQueries';
import SireAncestry from '../../components/SireAncestry';
import DamAncestry from '../../components/DamAncestry';
import { FaEdit, FaSpinner } from 'react-icons/fa';
import { compressImage, formatDate, getAge } from '../../helpers';
import type { AlpacaImage } from '../../models/AlpacaImage';
import AlpacaImageComponent from '../../components/AlpacaImageComponent';
import { useDropzone } from 'react-dropzone';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import AlpacaEventTable from '../../components/AlpacaEventTable';
import { handleError } from '../../apis';
import ShowPending from '../../components/ShowPending';
import ShowError from '../../components/ShowError';
import AppLayout from '../../components/AppLayout';

export default function AlpacaPage() {
  const { id } = useParams();
  const alpacaId = id ? +id : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: alpaca,
    isError,
    isPending,
    error,
  } = useQuery({
    queryKey: alpacaKeys.detail(alpacaId),
    queryFn: () => AlpacaApi.getAlpaca(alpacaId),
    enabled: !!id,
  });

  const saveImageMutation = useMutation({
    mutationFn: ({ alpacaId, file }: { alpacaId: number; file: string }) =>
      AlpacaApi.saveImage({ alpacaId, file }),
    onSuccess: (_, { alpacaId }) => {
      queryClient.invalidateQueries({
        queryKey: alpacaKeys.detail(alpacaId),
      });
    },
    onError: (error) => {
      console.error('Error saving image:', error);
      toast.error('Error saving image');
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: (fd: FormData) => AlpacaApi.uploadImage(fd),
    onSuccess: (data) => {
      saveImageMutation.mutate({
        alpacaId: alpaca?.alpacaId ?? 0,
        file: data.public_id,
      });
    },
    onError: (error) => {
      console.error('Error uploading image:', error);
      toast.error('Error uploading image');
    },
  });

  const handleDelete = async () => {
    const proceed = window.confirm(
      'Deleting an alpaca can not be undone. Are you sure?',
    );
    if (!proceed) return;
    try {
      await AlpacaApi.deleteAlpaca(alpacaId);
      queryClient.removeQueries({ queryKey: alpacaKeys.detail(alpacaId) });
      queryClient.invalidateQueries();
      navigate('/alpacas');
    } catch (error) {
      const message = handleError(error);
      toast.error(message);
    }
  };

  const uploadingImage =
    saveImageMutation.isPending || uploadImageMutation.isPending;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const compressedImage = await compressImage(acceptedFiles[0], true);

      const fd = new FormData();
      fd.append('upload_preset', import.meta.env.VITE_UPLOAD_PRESET);
      fd.append('file', compressedImage);
      uploadImageMutation.mutate(fd);
    },
    [uploadImageMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
    },
  });

  // if (!id) return redirect('/alpacas');
  // if (isPending) return <ShowPending isPending={isPending} />;
  // if (isError) return <ShowError error={error} isError={isError} />;
  return (
    <AppLayout>
      <ShowPending isPending={isPending} />
      <ShowError error={error} isError={isError} />
      <div className='w-full'>
        <div className='flex items-start justify-center'>
          <h3 className='text-center text-2xl dark:text-gray-300'>
            {alpaca?.name ?? ''}
          </h3>
          <Link
            to={`/update-alpaca/${alpaca?.alpacaId}`}
            className='ml-4 p-1 text-xs font-bold uppercase hover:opacity-70'
          >
            <div className='flex items-center gap-2'>
              <FaEdit /> Edit
            </div>
          </Link>
        </div>
        <div
          className={`mb-2 flex justify-center text-xs font-bold uppercase ${
            alpaca?.active ? '' : 'text-amber-500 dark:text-amber-200'
          }`}
        >
          {alpaca?.active ? 'Active' : 'Archived'}
        </div>

        <div
          id='stats-and-pic'
          className='grid grid-cols-[minmax(300px,1fr)_minmax(300px,1fr)] gap-4 max-md:grid-cols-1'
        >
          <div className='grid max-h-62.5 min-w-75 max-w-100 grid-cols-2 justify-self-end max-md:order-2 max-md:justify-self-center'>
            <p>DOB:</p>
            <p>{alpaca?.dateOfBirth && formatDate(alpaca?.dateOfBirth)}</p>
            <p>Age:</p>
            <p>{alpaca?.dateOfBirth && getAge(alpaca?.dateOfBirth)}</p>
            <p>Gender:</p>
            <p>{alpaca?.genderId && alpaca?.gender?.genderName}</p>
            <p>Color:</p>
            <p>{alpaca?.colorId && alpaca?.color?.colorName}</p>
            <p>Breed:</p>
            <p>{alpaca?.breedId && alpaca?.breed?.breedName}</p>
            <p>Farm:</p>
            <p>
              {alpaca?.farmId && (
                <Link
                  to={`/farms/${alpaca?.farm?.farmId}`}
                  className='font-bold'
                >
                  {alpaca?.farm?.name} <i className='fas fa-link' />
                </Link>
              )}
            </p>
            <p>Registry:</p>
            <p>
              {!alpaca?.registryNumber || alpaca?.registryNumber === 'null'
                ? ''
                : alpaca?.registryNumber}
            </p>
            <p>Chip Id:</p>
            <p>
              {!alpaca?.microchipId || alpaca?.microchipId === 'null'
                ? ''
                : alpaca?.microchipId}
            </p>
            <p>Chip Location:</p>
            <p>
              {!alpaca?.microchipLocation ||
              alpaca?.microchipLocation === 'null'
                ? ''
                : alpaca?.microchipLocation}
            </p>
            <p>Tag #:</p>
            <p>
              {!alpaca?.tagNumber || alpaca?.tagNumber === 'null'
                ? ''
                : alpaca?.tagNumber}
            </p>
          </div>
          <div
            id='a-image'
            className={`${
              (alpaca?.alpacaImages?.length ?? 0) > 0 ? 'w-full' : ''
            } justify-self-start max-md:justify-self-center`}
          >
            {alpaca &&
              alpaca?.alpacaImages &&
              [
                ...alpaca!.alpacaImages!.map(
                  (i: AlpacaImage, index: number) => (
                    <AlpacaImageComponent image={i} key={i.imageName + index} />
                  ),
                ),

                <div
                  key='drop-here'
                  className='flex h-62.5 w-62.5 items-center justify-center rounded-[20px] border border-gray-500 p-4'
                >
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    {uploadingImage ? (
                      <FaSpinner className='animate-spin ' />
                    ) : isDragActive ? (
                      <p>Drop the files here ...</p>
                    ) : (
                      <p>Drag 'n' drop an image, or click to select files</p>
                    )}
                  </div>
                </div>,
              ].slice(0, 1)}
          </div>
        </div>
        <div className='m-auto mt-2 max-w-154'>
          <span className='me-2  font-bold'>Note:</span>
          {alpaca?.note}
        </div>
        <div className='pt-4'>
          <h4>Events</h4>
          {alpaca?.alpacaEvents && (
            <AlpacaEventTable events={alpaca?.alpacaEvents ?? []} />
          )}
        </div>
        <SireAncestry alpacaId={alpacaId} />
        <DamAncestry alpacaId={alpacaId} />
        <div className='flex justify-end p-4'>
          <button
            className='my-2  rounded-md border border-rose-600 bg-rose-500 p-2 text-xs font-bold uppercase text-white hover:border-rose-700 hover:bg-rose-600 dark:border-rose-500 dark:bg-rose-500  dark:hover:border-rose-600  dark:hover:bg-rose-600 '
            type='button'
            title={`Delete ${alpaca?.name ?? 'Alpaca'}`}
            onClick={handleDelete}
          >
            Delete Alpaca
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
